import * as React from 'react';
import { createBlankTaskflow, createSeedTaskflows } from './seedData';
import { Taskflow, TaskflowTask } from './types';

export type TaskflowWizardStep = 'library' | 'nameScope' | 'taskList' | 'publish';

export interface TaskflowBuilderState {
    step: TaskflowWizardStep;
    library: Taskflow[];
    activeTaskflowId: string | null;
    activeTaskId: string | null;
}

type TaskflowBuilderAction =
    | { type: 'SET_STEP'; step: TaskflowWizardStep }
    | { type: 'OPEN_TASKFLOW'; taskflowId: string }
    | { type: 'START_NEW_TASKFLOW' }
    | { type: 'UPDATE_NAME_SCOPE'; name: string; matterCategory: string[]; matterType: string[] }
    | { type: 'REORDER_TASKS'; sourceIndex: number; destinationIndex: number }
    | { type: 'SELECT_TASK'; taskId: string }
    | { type: 'ADD_TASK'; task: TaskflowTask }
    | { type: 'UPDATE_TASK'; taskId: string; updates: Partial<TaskflowTask> }
    | { type: 'REMOVE_TASK'; taskId: string }
    | { type: 'REMOVE_TASKFLOW'; taskflowId: string }
    | { type: 'PUBLISH_ACTIVE_TASKFLOW' };

const initialState: TaskflowBuilderState = {
    step: 'library',
    library: createSeedTaskflows(),
    activeTaskflowId: null,
    activeTaskId: null,
};

let nextBlankTaskflowId = 1;

const withActiveTaskflow = (
    state: TaskflowBuilderState,
    updateTaskflow: (taskflow: Taskflow) => Taskflow,
): TaskflowBuilderState => ({
    ...state,
    library: state.library.map(taskflow =>
        taskflow.id === state.activeTaskflowId ? updateTaskflow(taskflow) : taskflow
    ),
});

const taskflowBuilderReducer = (state: TaskflowBuilderState, action: TaskflowBuilderAction): TaskflowBuilderState => {
    switch (action.type) {
        case 'SET_STEP':
            return { ...state, step: action.step };

        case 'OPEN_TASKFLOW':
            return { ...state, step: 'taskList', activeTaskflowId: action.taskflowId, activeTaskId: null };

        case 'START_NEW_TASKFLOW': {
            const blankTaskflow = createBlankTaskflow(`new-taskflow-${nextBlankTaskflowId++}`);
            return {
                ...state,
                step: 'nameScope',
                library: [...state.library, blankTaskflow],
                activeTaskflowId: blankTaskflow.id,
                activeTaskId: null,
            };
        }

        case 'UPDATE_NAME_SCOPE':
            return withActiveTaskflow(state, taskflow => ({
                ...taskflow,
                name: action.name,
                matterCategory: action.matterCategory,
                matterType: action.matterType,
            }));

        case 'REORDER_TASKS':
            return withActiveTaskflow(state, taskflow => {
                const reordered = Array.from(taskflow.taskOrder);
                const [movedTaskId] = reordered.splice(action.sourceIndex, 1);
                reordered.splice(action.destinationIndex, 0, movedTaskId);
                return { ...taskflow, taskOrder: reordered };
            });

        case 'SELECT_TASK':
            return { ...state, activeTaskId: action.taskId };

        case 'ADD_TASK':
            return withActiveTaskflow(state, taskflow => ({
                ...taskflow,
                taskOrder: [...taskflow.taskOrder, action.task.id],
                tasks: { ...taskflow.tasks, [action.task.id]: action.task },
            }));

        case 'UPDATE_TASK':
            return withActiveTaskflow(state, taskflow => {
                const existingTask = taskflow.tasks[action.taskId];
                if (!existingTask) {
                    return taskflow;
                }
                return {
                    ...taskflow,
                    tasks: { ...taskflow.tasks, [action.taskId]: { ...existingTask, ...action.updates } },
                };
            });

        case 'REMOVE_TASK': {
            const nextState = withActiveTaskflow(state, taskflow => {
                const { [action.taskId]: removedTask, ...remainingTasks } = taskflow.tasks;
                return {
                    ...taskflow,
                    taskOrder: taskflow.taskOrder.filter(taskId => taskId !== action.taskId),
                    tasks: remainingTasks,
                };
            });
            return {
                ...nextState,
                activeTaskId: state.activeTaskId === action.taskId ? null : state.activeTaskId,
            };
        }

        case 'REMOVE_TASKFLOW': {
            const isRemovingActiveTaskflow = state.activeTaskflowId === action.taskflowId;
            return {
                ...state,
                library: state.library.filter(taskflow => taskflow.id !== action.taskflowId),
                step: isRemovingActiveTaskflow ? 'library' : state.step,
                activeTaskflowId: isRemovingActiveTaskflow ? null : state.activeTaskflowId,
                activeTaskId: isRemovingActiveTaskflow ? null : state.activeTaskId,
            };
        }

        case 'PUBLISH_ACTIVE_TASKFLOW':
            return withActiveTaskflow(state, taskflow => ({ ...taskflow, status: 'published' }));

        default:
            return state;
    }
};

export interface TaskflowBuilderContextValue {
    state: TaskflowBuilderState;
    dispatch: React.Dispatch<TaskflowBuilderAction>;
    activeTaskflow: Taskflow | null;
    activeTask: TaskflowTask | null;
}

const TaskflowBuilderContext = React.createContext<TaskflowBuilderContextValue | undefined>(undefined);

export const TaskflowBuilderProvider: React.FC = ({ children }) => {
    const [state, dispatch] = React.useReducer(taskflowBuilderReducer, initialState);

    const activeTaskflow = state.library.find(taskflow => taskflow.id === state.activeTaskflowId) ?? null;
    const activeTask = (activeTaskflow && state.activeTaskId) ? activeTaskflow.tasks[state.activeTaskId] ?? null : null;

    const value: TaskflowBuilderContextValue = { state, dispatch, activeTaskflow, activeTask };

    return (
        <TaskflowBuilderContext.Provider value={value}>
            {children}
        </TaskflowBuilderContext.Provider>
    );
};

export const useTaskflowBuilder = (): TaskflowBuilderContextValue => {
    const context = React.useContext(TaskflowBuilderContext);
    if (!context) {
        throw new Error('useTaskflowBuilder must be used within a TaskflowBuilderProvider');
    }
    return context;
};
